// src/routes/Index.jsx
import React, { useEffect, useRef, useState } from "react";
import { Route, Routes, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { createSelector } from "reselect";

// Constants & Layouts
import { layoutTypes } from "../constants/layout";
import NonAuthLayout from "../Layout/NonAuthLayout";
import VerticalLayout from "../Layout/VerticalLayout";
import AuthProtected from "./AuthProtected";
import { getToken } from "../services/AuthToken";
import Error404 from "../Pages/Utility/Error404-Page";
import ApiErrorPage from "../Pages/Utility/ApiErrorPage";
import {
  clearApiErrorState,
  getApiErrorState,
  subscribeToApiErrorState,
} from "../helpers/apiErrorState";

// Routes
import { authProtectedRoutes, publicRoutes } from "./routes";

// Layout selector
const selectLayout = createSelector(
  (state) => state.Layout,
  (layout) => ({
    layoutType: layout.layoutType,
  })
);

// Resolve layout dynamically
const getLayout = (layoutType) => {
  switch (layoutType) {
    case layoutTypes.VERTICAL:
    default:
      return VerticalLayout;
  }
};

const Index = () => {
  const { layoutType } = useSelector(selectLayout);
  const Layout = getLayout(layoutType);
  const token = getToken();
  const location = useLocation();
  const [apiErrorState, setApiErrorState] = useState(() => getApiErrorState());
  const previousLocationRef = useRef(`${location.pathname}${location.search}`);

  useEffect(() => {
    const syncApiErrorState = () => {
      setApiErrorState(getApiErrorState());
    };

    return subscribeToApiErrorState(syncApiErrorState);
  }, []);

  useEffect(() => {
    const currentLocation = `${location.pathname}${location.search}`;
    if (previousLocationRef.current !== currentLocation && getApiErrorState()) {
      clearApiErrorState();
      setApiErrorState(null);
    }
    previousLocationRef.current = currentLocation;
  }, [location.pathname, location.search]);

  if (apiErrorState) {
    return token ? (
      <Layout>
        <ApiErrorPage />
      </Layout>
    ) : (
      <NonAuthLayout>
        <ApiErrorPage />
      </NonAuthLayout>
    );
  }

  return (
    <Routes>
      {/* Public Routes */}
      {publicRoutes.map(({ path, component: Component }, idx) => (
        <Route
          path={path}
          key={idx}
          element={
            <NonAuthLayout>
              <Component />
            </NonAuthLayout>
          }
        />
      ))}

      {/* Protected Routes */}
      {authProtectedRoutes.map(({ path, component: Component }, idx) => (
        <Route
          path={path}
          key={idx}
          element={
            <AuthProtected>
              <Layout>
                <Component />
              </Layout>
            </AuthProtected>
          }
        />
      ))}

      <Route
        path="*"
        element={
          token ? (
            <Layout>
              <Error404 />
            </Layout>
          ) : (
            <NonAuthLayout>
              <Error404 />
            </NonAuthLayout>
          )
        }
      />
    </Routes>
  );
};

export default Index;
